import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";

import { useAuthStore } from "./store/useAuthStore";
import { useFormStore } from "./store/useFormStore";

import LoginView from "./components/views/LoginView";
import { AdminView } from "./components/views/AdminView";
import { ManagementView } from "./components/views/ManagementView";
import { UserFormView } from "./components/views/UserFormView";
import ConfirmationView from "./components/views/ConfirmationView";
import DeceasedConfirmationView from "./components/views/DeceasedConfirmationView";
import { SindicatoView } from "./components/views/SindicatoView";

import { Header } from "./components/Header";
import { Toaster } from "sonner";

import { mapDataToForm } from "./lib/utils";
import bancos from "./lib/bancos.json";

function ProtectedRoute({ children, reqAdmin = false, reqSindicato = false }) {
  const { isAuthenticated, isAdminMode, isSindicatoMode } = useAuthStore();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (reqAdmin && !isAdminMode) return <Navigate to="/form" replace />;
  if (reqSindicato && !isSindicatoMode) return <Navigate to="/login" replace />;
  return children;
}

function AppRoutes() {
  const navigate = useNavigate();
  const { isLoggingIn, login, logout, isAdminMode, isSindicatoMode } = useAuthStore();

  const {
    userData, bancoData, setUserData, setBancoData,
    isSubmitting, handleUserDataChange, handleBancoChange,
    submitForm, submitCessao, selectedProcessIds, setSelectedProcessIds,
    processTypes, toggleProcessType, cessao, setCessao, clearForm,
    initializeProcesses
  } = useFormStore();

  const handleLoginSuccess = (data, cpfInput) => {
    const mapped = mapDataToForm(data, cpfInput);
    const bank = bancos.find((b) => b.nome === data.banco);
    const newBancoData = {
      nomeBanco: bank ? `${bank.codigo} - ${data.banco}` : data.banco || "",
      agencia: data.agencia || "",
      conta: data.conta_corrente ? `${data.conta_corrente}-${data.digito_conta}` : "",
      tipoPix: data.tipo_pix || "cpf",
      chavePix: data.pix || "",
      tipoConta: data.tipo_conta || "corrente",
    };
    setUserData(mapped);
    setBancoData(newBancoData);
    navigate("/form");
  };

  const onLogin = async (cpf) => {
    const res = await login(cpf);
    if (res?.success) {
      if (res.role === 'admin') navigate("/admin");
      else if (res.role === 'sindicato') navigate("/sindicato");
      else handleLoginSuccess(res.data, res.cpf);
    }
  };

  const onAdminUserSelectSuccess = (data) => {
    const mapped = mapDataToForm(data);
    const bank = bancos.find((b) => b.nome === data.banco);
    const newBancoData = {
      nomeBanco: bank ? `${bank.codigo} - ${data.banco}` : data.banco || "",
      agencia: data.agencia || "",
      conta: data.conta_corrente ? `${data.conta_corrente}-${data.digito_conta}` : "",
      tipoPix: data.tipo_pix || "cpf",
      chavePix: data.pix || "",
      tipoConta: data.tipo_conta || "corrente",
    };
    setUserData(mapped);
    setBancoData(newBancoData);
    navigate("/form");
  };

  const handleReturnToAdmin = () => {
    clearForm();
    navigate("/admin");
  };

  const handleLogout = () => {
    clearForm();
    logout();
    navigate("/login");
  };

  const handleSubmitWrapper = async (tipoAceite, isFalecido) => {
    const success = await submitForm(tipoAceite, isFalecido);
    return success;
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <Header isLoggedIn={useAuthStore(s => s.isAuthenticated)} onLogout={handleLogout} />
      <main className="flex flex-col flex-grow items-center justify-center">
        <Routes>
          <Route path="/" element={<Navigate to="/login" replace />} />

          <Route path="/login" element={
            <LoginView onLogin={onLogin} isLoading={isLoggingIn} />
          } />

          <Route path="/sindicato" element={
            <ProtectedRoute reqSindicato={true}>
              <SindicatoView />
            </ProtectedRoute>
          } />

          <Route path="/admin" element={
            <ProtectedRoute reqAdmin={true}>
              <AdminView onUserSelect={onAdminUserSelectSuccess} onGoToManagement={() => navigate("/admin/management")} />
            </ProtectedRoute>
          } />

          <Route path="/admin/management" element={
            <ProtectedRoute reqAdmin={true}>
              <ManagementView onBack={() => navigate("/admin")} />
            </ProtectedRoute>
          } />

          <Route path="/form" element={
            <ProtectedRoute>
              <UserFormView
                userData={userData}
                bancoData={bancoData}
                selectedProcessIds={selectedProcessIds}
                setSelectedProcessIds={setSelectedProcessIds}
                processTypes={processTypes}
                toggleProcessType={toggleProcessType}
                isSubmitting={isSubmitting}
                handleUserDataChange={handleUserDataChange}
                handleBancoChange={handleBancoChange}
                handleSubmit={handleSubmitWrapper}
                isAdminMode={isAdminMode}
                onReturnToAdmin={handleReturnToAdmin}
                onSubmitSuccess={() => navigate("/confirmation")}
                setCessao={setCessao}
                submitCessao={submitCessao}
                initializeProcesses={initializeProcesses}
              />
            </ProtectedRoute>
          } />

          <Route path="/confirmation" element={
            <ProtectedRoute>
              {userData?.falecido ? (
                <DeceasedConfirmationView
                  userData={userData}
                  isAdminMode={isAdminMode}
                  onReturnToAdmin={handleReturnToAdmin}
                  onExit={handleLogout}
                />
              ) : (
                <ConfirmationView
                  userData={userData}
                  isAdminMode={isAdminMode}
                  onReturnToAdmin={handleReturnToAdmin}
                  onExit={handleLogout}
                  cessao={cessao}
                />
              )}
            </ProtectedRoute>
          } />
        </Routes>
      </main>
      <footer className="text-center p-4 text-sm text-slate-500 border-t border-slate-200">
        © 2025 Juri Ativos. Todos os direitos reservados.
      </footer>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppRoutes />
      <Toaster position="top-right" richColors closeButton />
    </BrowserRouter>
  );
}

export default App;
